import { CertificationRequirementToggle } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/CertificationRequirementToggle.tsx';
import { ToggleRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/ToggleRequirementItem.tsx';
import { KycSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/styles.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import type { KycProofFlag } from '@dealstack/shared/lib/common/business/kycCertification';
import { Fragment } from 'react';

import {
	useNewJoinersAndAllocationsProofToggles,
	type ProofToggleGroup,
} from './useNewJoinersAndAllocationsProofToggles';
import type { NewJoinersAndAllocationsProofGroup } from './useNewJoinersAndAllocationsKYCController';
import { useKycCertificationEnabled } from '@dealstack/frontend/shared/hooks/useKycCertificationEnabled/useKycCertificationEnabled.hook.ts';

/** Shares Programme settings → KYC & AML's wording, so the two screens cannot drift apart. */
const PROOF_LABELS = {
	isProofOfIdentityRequired: {
		label: 'kyc_settings.proofs.identity.label',
		description: 'kyc_settings.proofs.identity.description',
	},
	isProofOfAddressRequired: {
		label: 'kyc_settings.proofs.address.label',
		description: 'kyc_settings.proofs.address.description',
	},
	isProofOfInvestmentVehicleRequired: {
		label: 'kyc_settings.proofs.vehicle.label',
		description: 'kyc_settings.proofs.vehicle.description',
	},
} as const;

interface Props {
	group: NewJoinersAndAllocationsProofGroup;
	flags: readonly KycProofFlag[];
	disabled?: boolean;
}

const ProofToggle = ({
	flag,
	toggles,
	disabled,
	certificationEnabled,
}: {
	flag: KycProofFlag;
	toggles: ProofToggleGroup;
	disabled?: boolean;
	certificationEnabled: boolean;
}) => {
	const tMipManage = useTranslationMipManage();

	return (
		<>
			<ToggleRequirementItem
				title={tMipManage(PROOF_LABELS[flag].label)}
				description={tMipManage(PROOF_LABELS[flag].description)}
				checked={toggles.isProofRequired(flag)}
				disabled={disabled}
				onToggle={() => toggles.toggleProof(flag)}
				showBorder={false}
			/>
			{certificationEnabled && toggles.isProofRequired(flag) && (
				<CertificationRequirementToggle
					checked={toggles.isCertificationRequired(flag)}
					disabled={disabled}
					onToggle={() => toggles.toggleCertification(flag)}
				/>
			)}
		</>
	);
};

/** The joiner step's proof requirements, laid out as Programme settings lays them out. */
export const NewJoinersAndAllocationsProofToggles = ({ group, flags, disabled }: Props) => {
	const toggles = useNewJoinersAndAllocationsProofToggles(group);
	const { certificationEnabled } = useKycCertificationEnabled();

	return (
		<KycSection $noMaxWidth>
			{flags.map((flag) => (
				<Fragment key={flag}>
					<ProofToggle
						flag={flag}
						toggles={toggles}
						disabled={disabled}
						certificationEnabled={certificationEnabled}
					/>
				</Fragment>
			))}
		</KycSection>
	);
};
